import React, { useState } from 'react';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import {
  alpha,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Paper,
  TextField,
  Theme,
  Typography,
  useTheme,
} from '@mui/material';

interface TimeSlot {
  time: string;
  available: boolean;
}

interface DaySchedule {
  date: string;
  day: string;
  slots: TimeSlot[];
}

interface ProductDetailTourSchedulingModalProps {
  open: boolean;
  onClose: () => void;
  propertyPrice?: number;
}

const getInputSx = (theme: Theme) => ({
  '& .MuiOutlinedInput-root': {
    color: theme.palette.text.primary,
    background: '#ffffff',
    '& fieldset': {
      borderColor: alpha(theme.palette.text.primary, 0.23),
    },
    '&:hover fieldset': {
      borderColor: alpha(theme.palette.text.primary, 0.4),
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.error.main,
    },
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
});

export const ProductDetailTourSchedulingModal = ({
  open,
  onClose,
  propertyPrice = 500000,
}: ProductDetailTourSchedulingModalProps) => {
  const theme = useTheme();
  const inputSx = getInputSx(theme);
  const panelSx = {
    background: 'var(--panel-tonal)',
    border: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
    borderRadius: 2,
  };

  const [selectedDate, setSelectedDate] = useState<string>('');
  const [selectedTime, setSelectedTime] = useState<string>('');
  const [selectedProperties, setSelectedProperties] = useState<string[]>([]);
  const [userInfo, setUserInfo] = useState({
    name: '',
    email: '',
    phone: '',
    notes: '',
  });

  const availableDates: DaySchedule[] = [
    {
      date: '2024-01-15',
      day: 'Mon',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: false },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
    {
      date: '2024-01-16',
      day: 'Tue',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: false },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
    {
      date: '2024-01-17',
      day: 'Wed',
      slots: [
        { time: '9:00 AM', available: false },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: false },
      ],
    },
    {
      date: '2024-01-18',
      day: 'Thu',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: true },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
    {
      date: '2024-01-19',
      day: 'Fri',
      slots: [
        { time: '9:00 AM', available: true },
        { time: '10:00 AM', available: false },
        { time: '11:00 AM', available: true },
        { time: '2:00 PM', available: true },
        { time: '3:00 PM', available: true },
        { time: '4:00 PM', available: true },
      ],
    },
  ];

  const comparableProperties = [
    {
      id: '1',
      address: '1234 Oak Street, Beverly Hills, CA',
      price: 485000,
      beds: 3,
      baths: 2,
      sqft: 1850,
    },
    {
      id: '2',
      address: '5678 Maple Ave, Beverly Hills, CA',
      price: 520000,
      beds: 3,
      baths: 2.5,
      sqft: 1950,
    },
    {
      id: '3',
      address: '9012 Pine Blvd, Beverly Hills, CA',
      price: 495000,
      beds: 3,
      baths: 2,
      sqft: 1800,
    },
  ];

  const handlePropertyToggle = (propertyId: string) => {
    setSelectedProperties((prev) =>
      prev.includes(propertyId)
        ? prev.filter((id) => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  const getSelectedDateSchedule = () => {
    return availableDates.find((date) => date.date === selectedDate);
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totalCreditsNeeded = selectedProperties.length + 1;
  const hasEnoughCredits = totalCreditsNeeded <= 6;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        className: 'taste-rise taste-panel',
        sx: {
          background: '#ffffff',
          borderRadius: 3,
          height: '90vh',
          boxShadow: `0 24px 64px ${alpha(theme.palette.common.black, 0.12)}`,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        component="div"
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: '#ffffff',
          px: 3,
          py: 2.5,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <CalendarTodayIcon sx={{ color: theme.palette.error.main }} />
          <Box>
            <Typography
              variant="h5"
              fontWeight={700}
              className="font-display"
              sx={{
                color: theme.palette.text.primary,
                letterSpacing: '-0.02em',
              }}
            >
              Schedule Tour
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: theme.palette.text.secondary, mt: 0.25 }}
            >
              Tour this property + comparable
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Chip
            label="6 Credits Available"
            size="small"
            sx={{
              bgcolor: alpha(theme.palette.error.main, 0.08),
              color: theme.palette.text.primary,
              fontWeight: 600,
              border: `1px solid ${alpha(theme.palette.error.main, 0.15)}`,
            }}
          />
          <IconButton
            onClick={onClose}
            size="small"
            sx={{ color: theme.palette.text.primary }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        sx={{ p: 3, background: '#ffffff', flex: 1, overflowY: 'auto' }}
      >
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              gutterBottom
              className="font-display"
              fontWeight={600}
              sx={{ color: theme.palette.text.primary }}
            >
              Select Date & Time
            </Typography>

            <Box sx={{ mb: 3 }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ color: theme.palette.text.secondary }}
              >
                Available Dates
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {availableDates.map((date) => (
                  <Paper
                    key={date.date}
                    elevation={0}
                    onClick={() => setSelectedDate(date.date)}
                    sx={{
                      p: 2,
                      minWidth: 80,
                      textAlign: 'center',
                      cursor: 'pointer',
                      background:
                        selectedDate === date.date
                          ? alpha(theme.palette.error.main, 0.1)
                          : '#ffffff',
                      border: `1px solid ${
                        selectedDate === date.date
                          ? theme.palette.error.main
                          : alpha(theme.palette.text.primary, 0.12)
                      }`,
                      borderRadius: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: alpha(theme.palette.error.main, 0.06),
                        borderColor: theme.palette.error.main,
                      },
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {date.day}
                    </Typography>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {new Date(date.date).getDate()}
                    </Typography>
                  </Paper>
                ))}
              </Box>
            </Box>

            {selectedDate && (
              <Box sx={{ mb: 3 }}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Available Times
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {getSelectedDateSchedule()?.slots.map((slot) => (
                    <Button
                      key={slot.time}
                      variant={
                        selectedTime === slot.time ? 'contained' : 'outlined'
                      }
                      disabled={!slot.available}
                      onClick={() => setSelectedTime(slot.time)}
                      sx={{
                        minWidth: 100,
                        color: slot.available
                          ? selectedTime === slot.time
                            ? '#ffffff'
                            : theme.palette.text.primary
                          : theme.palette.text.disabled,
                        borderColor: slot.available
                          ? alpha(theme.palette.text.primary, 0.23)
                          : alpha(theme.palette.text.primary, 0.12),
                        bgcolor:
                          selectedTime === slot.time
                            ? theme.palette.error.main
                            : '#ffffff',
                        '&:hover': {
                          bgcolor: slot.available
                            ? selectedTime === slot.time
                              ? theme.palette.error.dark
                              : alpha(theme.palette.error.main, 0.06)
                            : '#ffffff',
                          borderColor: slot.available
                            ? theme.palette.error.main
                            : alpha(theme.palette.text.primary, 0.12),
                        },
                      }}
                    >
                      {slot.time}
                    </Button>
                  ))}
                </Box>
              </Box>
            )}

            <Box>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ color: theme.palette.text.secondary }}
              >
                Contact Information
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Name"
                    value={userInfo.name}
                    onChange={(e) =>
                      setUserInfo((prev) => ({ ...prev, name: e.target.value }))
                    }
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Email"
                    type="email"
                    value={userInfo.email}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        email: e.target.value,
                      }))
                    }
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone"
                    value={userInfo.phone}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        phone: e.target.value,
                      }))
                    }
                    sx={inputSx}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label="Special Notes or Requests"
                    value={userInfo.notes}
                    onChange={(e) =>
                      setUserInfo((prev) => ({
                        ...prev,
                        notes: e.target.value,
                      }))
                    }
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </Box>
          </Grid>

          <Grid item xs={12} md={6}>
            <Typography
              variant="h6"
              gutterBottom
              className="font-display"
              fontWeight={600}
              sx={{ color: theme.palette.text.primary }}
            >
              Select Properties to Tour
            </Typography>

            <Paper
              elevation={0}
              sx={{
                p: 2,
                mb: 2,
                ...panelSx,
                borderColor: alpha(theme.palette.error.main, 0.2),
              }}
            >
              <Box
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}
              >
                <LocationOnIcon sx={{ color: theme.palette.error.main }} />
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  sx={{ color: theme.palette.text.primary }}
                >
                  Main Property (Included)
                </Typography>
              </Box>
              <Typography
                variant="body2"
                gutterBottom
                sx={{ color: theme.palette.text.secondary }}
              >
                123 Main Street, Beverly Hills, CA
              </Typography>
              <Typography
                variant="body2"
                fontWeight={600}
                sx={{ color: theme.palette.text.primary }}
              >
                {formatCurrency(propertyPrice)}
              </Typography>
            </Paper>

            <Typography
              variant="subtitle2"
              gutterBottom
              sx={{ color: theme.palette.text.secondary }}
            >
              Comparable Properties (Select additional)
            </Typography>

            {comparableProperties.map((property) => (
              <Paper
                key={property.id}
                elevation={0}
                onClick={() => handlePropertyToggle(property.id)}
                sx={{
                  p: 2,
                  mb: 2,
                  cursor: 'pointer',
                  background: selectedProperties.includes(property.id)
                    ? alpha(theme.palette.error.main, 0.08)
                    : '#ffffff',
                  border: `1px solid ${
                    selectedProperties.includes(property.id)
                      ? theme.palette.error.main
                      : alpha(theme.palette.text.primary, 0.08)
                  }`,
                  borderRadius: 2,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    background: alpha(theme.palette.error.main, 0.04),
                    borderColor: theme.palette.error.main,
                  },
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                  }}
                >
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      gutterBottom
                      sx={{ color: theme.palette.text.secondary }}
                    >
                      {property.address}
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {property.beds} beds
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {property.baths} baths
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary }}
                      >
                        {property.sqft} sqft
                      </Typography>
                    </Box>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      sx={{ color: theme.palette.text.primary }}
                    >
                      {formatCurrency(property.price)}
                    </Typography>
                  </Box>
                  <Chip
                    label="1 Credit"
                    size="small"
                    sx={{
                      bgcolor: selectedProperties.includes(property.id)
                        ? theme.palette.error.main
                        : alpha(theme.palette.error.main, 0.1),
                      color: selectedProperties.includes(property.id)
                        ? '#ffffff'
                        : theme.palette.text.primary,
                      fontSize: '0.7rem',
                    }}
                  />
                </Box>
              </Paper>
            ))}

            <Paper elevation={0} sx={{ p: 2, ...panelSx }}>
              <Typography
                variant="subtitle2"
                gutterBottom
                sx={{ color: theme.palette.text.primary }}
              >
                Credits Summary
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Total Credits Used
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  sx={{
                    color: hasEnoughCredits
                      ? theme.palette.text.primary
                      : theme.palette.error.main,
                  }}
                >
                  {totalCreditsNeeded} / 6
                </Typography>
              </Box>
              <Typography
                variant="caption"
                display="block"
                sx={{ mt: 1, color: theme.palette.text.secondary }}
              >
                {hasEnoughCredits
                  ? `${6 - totalCreditsNeeded} credits remaining`
                  : 'Not enough credits. Purchase more credits to continue.'}
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions
        sx={{
          p: 3,
          borderTop: `1px solid ${alpha(theme.palette.text.primary, 0.08)}`,
          background: '#ffffff',
        }}
      >
        <Button onClick={onClose} sx={{ color: theme.palette.text.secondary }}>
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={
            !selectedDate ||
            !selectedTime ||
            !hasEnoughCredits ||
            !userInfo.name ||
            !userInfo.email
          }
          sx={{
            fontWeight: 700,
            backgroundColor: theme.palette.error.main,
            color: '#ffffff',
            '&:hover': {
              backgroundColor: '#c2185b',
            },
            '&.Mui-disabled': {
              backgroundColor: alpha(theme.palette.text.primary, 0.12),
              color: alpha(theme.palette.text.primary, 0.38),
            },
          }}
        >
          Schedule Tour
        </Button>
      </DialogActions>
    </Dialog>
  );
};
